import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { FormCreator, FormFieldDef } from "@/components/shared/form-creator";
import { toast } from "sonner";
import { organization_type } from "@/models/organization.model";
import { useSignup } from "@/app/auth/signup/signup.hooks.ts";
import { useWizard } from "@/app/auth/signup/signup.components/wizard/wizard.hooks.ts";
import { useNavigate } from "react-router";

const individualInviteSchema = z
  .object({
    name: z.string().min(2),
    email: z.string().email(),
    phone: z.string().optional(),
    password: z.string().min(8),
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, {
    path: ["confirm"],
    message: "Passwords must match",
  });

type IndividualInviteValues = z.infer<typeof individualInviteSchema>;

const fields: FormFieldDef[] = [
  { type: "text", name: "name", label: "Full name", required: true },
  { type: "email", name: "email", label: "Email", required: true },
  { type: "text", name: "phone", label: "Phone" },
  { type: "password", name: "password", label: "Password", required: true },
  { type: "password", name: "confirm", label: "Confirm password", required: true },
];

export function IndividualInviteSignupForm({
  prefilledEmail,
  inviteToken
}: {
  prefilledEmail: string;
  inviteToken: string;
}) {
  const { saving, error, submit, needsVerification } = useSignup("orgInvite", inviteToken);
  const { inviterName, inviteeName } = useWizard();
  const navigate = useNavigate();

  const form = useForm<IndividualInviteValues>({
    resolver: zodResolver(individualInviteSchema),
    defaultValues: {
      email: prefilledEmail,
      name: inviteeName ?? "",
      phone: "",
      password: "",
      confirm: "",
    },
  });

  const onSubmit = async ({ name, email, phone, password }: IndividualInviteValues) => {
    try {
      await submit({ name, email, phone, password, organizationType: organization_type.emp });

      toast.success("Individual account created!");

      if (!needsVerification) navigate("/");
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Signup failed");
    }
  };

  return (
    <>
      <div className="flex flex-col items-start gap-2 text-center mb-6">
        <h1 className="text-2xl font-bold">Complete your individual account</h1>
        <p className="text-balance text-sm text-muted-foreground">
          You've been invited to join {inviterName || "the team"} as an individual
        </p>
      </div>

      {error && <p className="text-sm text-destructive mb-2">{error}</p>}
      <FormCreator
        form={form}
        formDefinition={fields}
        buttonText={saving ? "Creating…" : "Create individual account"}
        onSubmit={onSubmit}
      />
    </>
  );
}
